import { ComponentProps, ReactNode, SVGProps } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { ButtonStylesVariants, buttonStyles } from './variants';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../typography/typography';
import { match } from 'ts-pattern';
import { Spinner } from '../spinner/spinner';

export type ButtonProps = {
  children: ReactNode;
  className?: string;
  isLoading?: boolean;
  iconLeft?: (props: SVGProps<SVGSVGElement>) => ReactNode;
  iconRight?: (props: SVGProps<SVGSVGElement>) => ReactNode;
} & Omit<ComponentProps<typeof AriaButton>, 'className' | 'children'> &
  ButtonStylesVariants;

export function Button(props: ButtonProps) {
  const {
    className,
    children,
    intent,
    isLoading = false,
    size = 'default',
    iconLeft: IconLeft,
    iconRight: IconRight,
    ...rest
  } = props;

  return (
    <AriaButton
      className={twMerge(buttonStyles({ intent, size }), className)}
      {...rest}
    >
      {IconLeft && (
        <IconLeft
          className={twMerge(
            mapSizeToIconSize(size),
            mapIntentToIconColor(intent),
            'mr-4',
          )}
        />
      )}

      <Typography
        as="span"
        variant={mapSizeToTypographyVariant(size)}
        weight="medium"
        useCase="button"
        className="text-currentColor"
      >
        {children}
      </Typography>

      {isLoading ? (
        <div role="status" className="pl-8">
          <Spinner />
        </div>
      ) : (
        IconRight && (
          <IconRight
            className={twMerge(
              mapSizeToIconSize(size),
              mapIntentToIconColor(intent),
              'ml-4',
            )}
          />
        )
      )}
    </AriaButton>
  );
}

export function mapSizeToTypographyVariant(
  size: ButtonStylesVariants['size'],
): ComponentProps<typeof Typography>['variant'] {
  return match(size)
    .returnType<ComponentProps<typeof Typography>['variant']>()
    .with('small', () => 'body-s')
    .with('default', () => 'body-m')
    .with('large', () => 'body-l')
    .otherwise(() => 'body-m');
}

export function mapSizeToIconSize(size: ButtonStylesVariants['size']): string {
  return match(size)
    .returnType<string>()
    .with('small', () => 'min-w-16 w-16 h-16')
    .with('default', () => 'min-w-20 w-20 h-20')
    .with('large', () => 'min-w-24 w-24 h-24')
    .otherwise(() => 'min-w-24 w-24 h-24');
}

export function mapIntentToIconColor(
  intent: ButtonStylesVariants['intent'],
): string {
  return match(intent)
    .returnType<string>()
    .with('secondary', () => 'fill-primaryMain')
    .with('tertiary', () => 'fill-primaryMain')
    .otherwise(() => 'fill-grayscale0');
}
